import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Hotel, ShieldCheck, User, MessageSquare, Inbox, Building2, Wallet, Gift, Sparkles, LifeBuoy, Plane, Bot } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import PropertySwitcher from "@/components/PropertySwitcher";

/**
 * Navbar — MakeMyTrip-parity top navigation that auto-adapts:
 *   - On Landing / dark-hero pages: transparent + white ink at top,
 *     collapses to premium solid dark on scroll.
 *   - On regular pages: solid white with subtle border.
 */
export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Landing page uses the dark hero → nav starts transparent
  const darkContext = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Iter36 fix — the Landing page's dark hero starts BELOW the nav, so a
  // transparent nav at scrollY=0 shows the white body through. That plus a
  // `brightness-0 invert` logo makes the logo invisible on white. Always use
  // the solid dark chrome on Landing regardless of scroll position.
  const useDarkChrome = false;
  const useLuxDark    = darkContext; // Landing → always solid dark

  const shellClass = useDarkChrome
    ? "sticky top-0 z-40 bg-transparent border-b border-transparent"
    : useLuxDark
      ? "sticky top-0 z-40 bg-[#0F1115]/95 backdrop-blur-md border-b border-white/10"
      : "sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200";

  const linkClass = (useDarkChrome || useLuxDark)
    ? "text-white/85 hover:text-[#E8D8A8] transition-colors"
    : "text-slate-700 hover:text-slate-900 hw-link";

  const goDash = () => {
    if (!user) return navigate("/login");
    if (user.role === "admin") return navigate("/admin");
    if (user.role === "hotel_partner") return navigate("/partner");
    if (user.role === "hotel_partner_staff") return navigate("/partner");
    return navigate("/customer");
  };

  return (
    <header className={shellClass}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-24 md:h-28">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className={`inline-flex items-center rounded-2xl ${useLuxDark ? "bg-white px-4 py-2 shadow-[0_10px_30px_-8px_rgba(200,163,77,0.55)]" : ""}`}>
            <img
              src="/logo.png"
              alt="SwaanStay — India's First AI-Powered Hotel Marketplace · Connecting Travellers Directly with Hotels"
              className="h-16 md:h-20 w-auto block"
              loading="eager"
              fetchPriority="high"
            />
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold">
          <Link to="/search" data-testid="nav-search" className={linkClass}>Find Hotels</Link>
          <Link to="/requests/browse" data-testid="nav-marketplace" className={linkClass}>Group Bookings</Link>
          <Link to="/partner-cta" data-testid="nav-partner-cta" className={linkClass}>List Your Hotel</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user && (user.role === "hotel_partner" || user.role === "hotel_partner_pending") && (
            <PropertySwitcher dark={useDarkChrome || useLuxDark} />
          )}
          {user && <NotificationBell />}
          {!user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                className={useDarkChrome || useLuxDark ? "text-white hover:bg-white/10 hover:text-white" : ""}
              >
                Log in
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register/traveller")}
                data-testid="nav-signup-btn"
                className="ss-btn ss-btn-primary rounded-xl px-4 py-2 h-auto text-sm"
              >
                Sign up
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-xl transition-colors ${
                    useDarkChrome || useLuxDark ? "hover:bg-white/10" : "hover:bg-slate-100"
                  }`}
                >
                  <Avatar className="h-8 w-8 rounded-xl">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="rounded-xl bg-[#0F1115] text-[#E8D8A8] text-xs font-bold">
                      {(user.name || "U").slice(0, 1)}
                    </AvatarFallback>
                  </Avatar>
                  <span className={`hidden md:block text-sm font-semibold max-w-[140px] truncate ${
                    useDarkChrome || useLuxDark ? "text-white" : "text-slate-900"
                  }`}>{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500">Signed in as</div>
                  <div className="font-semibold truncate">{user.email}</div>
                  <div className="hw-label text-[9px] mt-1 text-[#A6852F]">{user.role || "no role"}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={goDash} data-testid="menu-dashboard">
                  {user.role === "admin" ? <ShieldCheck className="w-4 h-4 mr-2" /> : user.role === "hotel_partner" ? <Hotel className="w-4 h-4 mr-2" /> : <User className="w-4 h-4 mr-2" />}
                  Dashboard
                </DropdownMenuItem>
                {user.role === "customer" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/trips")} data-testid="menu-trips"><Plane className="w-4 h-4 mr-2" />My Trips</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/negotiations")} data-testid="menu-negotiations"><Inbox className="w-4 h-4 mr-2" />My Negotiations</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/requests")} data-testid="menu-requests"><Building2 className="w-4 h-4 mr-2" />Group Booking Bids</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/bookings")} data-testid="menu-bookings"><Hotel className="w-4 h-4 mr-2" />All Bookings</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/chats")} data-testid="menu-chats"><MessageSquare className="w-4 h-4 mr-2" />Chats</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet"><Wallet className="w-4 h-4 mr-2" />My Wallet</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/rewards")} data-testid="menu-rewards"><Sparkles className="w-4 h-4 mr-2" />Rewards</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/referrals")} data-testid="menu-referrals"><Gift className="w-4 h-4 mr-2" />Refer & Earn</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><User className="w-4 h-4 mr-2" />Settings</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/support")} data-testid="menu-support"><LifeBuoy className="w-4 h-4 mr-2" />Support</DropdownMenuItem>
                  </>
                )}
                {user.role === "hotel_partner" && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/partner/leads")} data-testid="menu-leads"><Inbox className="w-4 h-4 mr-2" />Leads</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/partner/reservations")} data-testid="menu-reservations"><Hotel className="w-4 h-4 mr-2" />Reservations</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/partner/rooms")} data-testid="menu-rooms"><Hotel className="w-4 h-4 mr-2" />Rooms</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/partner/ai")} data-testid="menu-ai-studio"><Sparkles className="w-4 h-4 mr-2 text-[#A6852F]" />AI Studio</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/partner/ai-drafts")} data-testid="menu-ai-drafts"><Bot className="w-4 h-4 mr-2 text-[#16A34A]" />AI Bid Drafts</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/partner/profile")} data-testid="menu-profile"><Building2 className="w-4 h-4 mr-2" />Hotel Profile</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/chats")} data-testid="menu-partner-chats"><MessageSquare className="w-4 h-4 mr-2" />Chats</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-partner-settings"><User className="w-4 h-4 mr-2" />Notification Prefs</DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
